import React, { useState, useCallback } from "react";
import { useVocabData } from "../hooks/useVocabData";
import { useVocabNavigation } from "../hooks/useVocabNavigation";
import { useSpeechRecognition } from "../hooks/useSpeechRecognition";
import { audioService } from "../services/audioService";
import { analyzeResponse, getAnalysisColorClasses } from "../utils/analysisUtils";
import { VOCAB_CONSTANTS } from "../constants/vocabConstants";
import HelpNotice from "../components/Notices/HelpNotice";
import ChapterNavigation from "../components/Navigation/ChapterNavigation";
import WordDisplayCard from "../components/Cards/WordDisplayCard";
import ExampleSentences from "../components/Sentences/ExampleSentences";
import PracticeSection from "../components/Practice/PracticeSection";
import { useWordInstructionAudio } from "../hooks/useInstructionAudio";
import MainContainer from "../components/Containers/MainContainer";
import ContentContainer from "../components/Containers/ContentContainer";

const VocabLearn3 = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const { data, isLoading, isError, error } = useVocabData(pageIndex);
  const { words, wordsNative, sentences, sentencesNative } = data || {};
  const { currentIndex, handleNext, handlePrev, resetIndex } =
    useVocabNavigation(words?.length);
  const {
    data: audioUrl,
    isLoading: isAudioLoading,
    isError: isAudioError,
    error: audioError,
  } = useWordInstructionAudio(
    pageIndex * VOCAB_CONSTANTS.WORDS_PER_CHAPTER + currentIndex
  );

  const { isListening, startListening } = useSpeechRecognition();

  // Local state
  const [dialogState, setDialogState] = useState(
    VOCAB_CONSTANTS.DEFAULT_DIALOG_STATE
  );
  const [userResponse, setUserResponse] = useState("");
  const [analysisReport, setAnalysisReport] = useState(null);

  // Handlers
  const handleNavigation = (direction) => {
    if (direction === "next") {
      handleNext();
    } else {
      handlePrev();
    }
    setUserResponse("");
    setAnalysisReport(null);
  };

  const handleListenToInstruction = () => {
    audioService.playAudio(audioUrl);
  };

  const handleStartListening = useCallback(() => {
    startListening(
      (transcript) => setUserResponse(transcript),
      (state) => setDialogState(state)
    );
  }, [startListening]);

  const handleAnalyzeResponse = useCallback(() => {
    const result = analyzeResponse(userResponse, words[currentIndex]);
    setAnalysisReport(result);
    setDialogState(VOCAB_CONSTANTS.DEFAULT_DIALOG_STATE);
  }, [userResponse, words, currentIndex]);

  const handlePageChange = (pageIndex) => {
    setPageIndex(pageIndex);
    resetIndex();
    setUserResponse("");
    setAnalysisReport(null);
  };

  // Early return for loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <MainContainer>
      <ContentContainer>
        <HelpNotice />

        <ChapterNavigation
          currentPage={pageIndex}
          onPageChange={handlePageChange}
          length={VOCAB_CONSTANTS.TOTAL_CHAPTERS}
          loading={isLoading}
        />

        <div className="grid grid-cols-1 gap-8">
          <WordDisplayCard
            text={words[currentIndex]}
            subText="ইংরেজি শব্দ"
            textNative={wordsNative[currentIndex]}
            currentIndex={currentIndex}
            totalWords={words.length}
            onNext={() => handleNavigation("next")}
            onPrev={() => handleNavigation("prev")}
            onListen={handleListenToInstruction}
          />

          <ExampleSentences
            englishSentence={sentences[currentIndex]}
            bengaliSentence={sentencesNative[currentIndex]}
          />

          <PracticeSection
            dialogState={dialogState}
            isListening={isListening}
            userResponse={userResponse}
            analysisReport={analysisReport ? analysisReport.message : null}
            onStartListening={handleStartListening}
            onAnalyze={handleAnalyzeResponse}
            colorClass={getAnalysisColorClasses(analysisReport?.type)}
          />
        </div>
      </ContentContainer>
    </MainContainer>
  );
};

export default VocabLearn3;
